import { Link } from '@tanstack/react-router'
import { cn } from '../../lib/cn'

interface ZoneProps {
  /** Roman numeral or short index, set in the display serif. */
  index: string
  title: string
  description: string
  /** Makes the whole tile a link to a member area. */
  to?: string
  className?: string
}

/**
 * A member-area tile (components card: `.zone`) — the three doors off the
 * clubhouse. Renders as a link when `to` is given, a plain panel otherwise.
 *
 * The whole tile is the target rather than a "read more" inside it, so the
 * accessible name is the title, and the lift on hover is suppressed under
 * reduced motion.
 */
export function Zone({ index, title, description, to, className }: ZoneProps) {
  const body = (
    <>
      <span className="font-display text-[13px] text-brass-deep">{index}</span>
      <h3 className="mb-2.5 mt-1.5 font-display text-[22px] font-medium leading-tight text-pine">
        {title}
      </h3>
      <p className="flex-1 text-sm text-ink-soft">{description}</p>
    </>
  )

  const shell = cn(
    'flex min-h-[210px] flex-col rounded-club-lg border border-ink/12 bg-parchment p-7',
    className,
  )

  if (!to) {
    return <div className={shell}>{body}</div>
  }

  return (
    <Link
      to={to}
      className={cn(
        shell,
        'transition duration-150 ease-out hover:-translate-y-0.5 hover:shadow-[0_20px_44px_-26px_rgba(21,58,42,0.4)]',
        'motion-reduce:transition-none motion-reduce:hover:translate-y-0',
      )}
    >
      {body}
    </Link>
  )
}
